/**
 * WHY AN ARRIVING EMAIL NEVER REACHED A PERSON — the desk's reading of the
 * inbound triage verdict.
 *
 * A vocabulary mirror and nothing else: no request, no state, no judgement.
 * The server owns the decision (`Constants/InboundTriage.ts`) and this holds
 * the words the queue says it in, the same arrangement
 * `Utils/orderLineKinds.js` has for the ledger's enum.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE SETS ARE CLOSED AND ARE COPIED VERBATIM. DO NOT ADD A LABEL OF YOUR
 * OWN, AND DO NOT BUILD A SENDER BLOCKLIST BESIDE THEM.
 * ══════════════════════════════════════════════════════════════════════════
 *
 * They are closed on the server for a reason this codebase has already paid
 * for: `booking.category` is free text with 170 spellings and ~36% of the
 * volume is lost to any per-category analysis that does not normalise first.
 * A funnel whose drop reasons cannot be grouped is a funnel nobody can read —
 * and a gate quietly eating customer enquiries looks EXACTLY like a gate that
 * is working until somebody can count the reasons apart. A thirteenth label
 * invented here would be a category the server cannot count and the API
 * cannot filter.
 *
 * A third classifier in the browser would be worse still. Two already exist
 * (the header gates and the model) and they disagree in measured ways: over
 * 294 recent objects the header gates alone caught neither AWS SNS
 * notifications nor Trustpilot — only the model did. A sender blocklist in
 * this layer would drift from both of them and would be the copy nobody
 * remembers to update.
 *
 * ── TWO LEVELS, AND THEY ARE NOT THE SAME FIELD ──
 * `drop_reason` is ONE of nine values and is what the row records. Eight of
 * them are deterministic header reads; the ninth, `not_eligible`, means THE
 * MODEL judged it, and the model's own four categories are a SEPARATE field
 * (`output.kinds` on the eligibility step). They are not interchangeable and
 * there are not thirteen drop reasons — so this file keeps them as two lists
 * and the filter composes them rather than flattening them into one.
 */

/**
 * THE NINE. Eight header gates in the order the server evaluates them, then
 * the model's umbrella.
 *
 * `detail` is what the desk needs to know beyond the word, and it is the
 * server's own explanation rather than a paraphrase.
 */
export const DROP_REASONS = [
  { value: 'too_old', label: 'Older than triage', detail: 'it arrived before triage was switched on' },
  { value: 'other_mailbox', label: 'Another mailbox', detail: 'it did not arrive on the mailbox this phase covers' },
  { value: 'spam', label: 'Spam', detail: 'SES’s own spam verdict at receipt was FAIL' },
  { value: 'virus', label: 'Virus', detail: 'SES’s own virus verdict at receipt was FAIL' },
  { value: 'list_unsubscribe', label: 'Mailing list', detail: 'it carries a List-Unsubscribe header' },
  { value: 'precedence_bulk', label: 'Bulk', detail: 'its Precedence header says bulk, junk or list' },
  { value: 'list_id', label: 'From a list', detail: 'it carries a List-Id header' },
  { value: 'auto_submitted', label: 'Auto-sent', detail: 'its Auto-Submitted header says it was generated' },
  { value: 'not_eligible', label: 'The model judged it', detail: 'no header gave it away; the model read it and said it is not a customer who needs an answer' },
];

/** The one drop reason that is a model judgement rather than a header read. */
export const MODEL_JUDGED = 'not_eligible';

/**
 * WHAT THE MODEL MAY NAME — its four categories, which sit UNDER
 * `not_eligible` rather than beside the other eight.
 *
 * Deliberately nothing here about the CONTENT of an enquiry: "too
 * complicated", "needs a person", "about a complaint" are judgements about
 * the reply and belong to the agent that writes it, not to the gate in front
 * of it. The server states that rule where the set is defined, and it is
 * worth knowing here too — it is why a refusal is never a routing decision.
 */
export const INELIGIBLE_KINDS = [
  { value: 'bulk_or_marketing', label: 'Newsletter or campaign' },
  { value: 'automated_notification', label: 'Machine-generated' },
  { value: 'not_for_us', label: 'Not a customer enquiry' },
  { value: 'nothing_to_answer', label: 'Nothing asked' },
];

/** A descriptor by value, so a member the server adds still renders. */
const descriptor = (list, value) => (
  list.find((entry) => entry.value === value)
  || (value ? { value, label: String(value), detail: '' } : null)
);

export const dropReasonDescriptor = (value) => descriptor(DROP_REASONS, value);
export const ineligibleKindDescriptor = (value) => descriptor(INELIGIBLE_KINDS, value);

/**
 * Why this conversation was filed, in one line fit for a badge.
 *
 * The model's umbrella is replaced by the kinds it actually named when it
 * named any — "The model judged it" tells a reader nothing they can act on,
 * and "Newsletter or campaign" tells them the gate did its job. The umbrella
 * stands alone when the kinds were unreadable, which is a real case: the
 * server drops a kind it does not recognise.
 */
export const filingReason = (triage) => {
  if (!triage?.drop_reason) return '';
  const reason = dropReasonDescriptor(triage.drop_reason);
  const kinds = (triage.kinds || []).map((kind) => ineligibleKindDescriptor(kind)?.label);
  const named = kinds.filter(Boolean);

  if (triage.drop_reason === MODEL_JUDGED && named.length > 0) return named.join(', ');
  return reason?.label || '';
};

/**
 * THE QUEUE'S TRIAGE FILTER — one select, over the loaded page.
 *
 * It composes the two levels rather than flattening them: picking one of the
 * model's four means "the model refused it AND it named this", which is two
 * fields and one option. Filtered in the browser over the entry the console
 * already holds, exactly as the desk views are, so this needs nothing indexed
 * server-side and the API shape stays the one the funnel already records.
 *
 * `any` is first so the Filed view opens on the whole cabinet.
 */
export const FILING_FILTERS = [
  { value: '', label: 'Any reason' },
  ...DROP_REASONS.map(({ value, label }) => ({ value, label })),
  ...INELIGIBLE_KINDS.map(({ value, label }) => ({ value, label: `… ${label}` })),
];

/** Does this verdict match the chosen filing filter? Empty matches anything. */
export const filingMatches = (filter, triage) => {
  if (!filter) return true;
  if (triage?.drop_reason === filter) return true;
  return triage?.drop_reason === MODEL_JUDGED && (triage.kinds || []).includes(filter);
};

export default DROP_REASONS;
